import "../App.css";
import { useEffect, useState, useRef } from "react";
import Cards from "../components/Card/Cards";
import Box from "@mui/material/Box";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import React from "react";
import Footer from "../components/Footer/Footer";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import Stack from "@mui/material/Stack";
import Header from "../components/Header/Header";
import { fetchProducts } from "../services/getProducts";
import { fetchCategories } from "../services/getCategories";

const filterLabelSx = {
  fontSize: "0.72rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#6f6f74",
  mb: 1.5,
};

const filterRowSx = {
  ml: 0,
  mr: 0,
  width: "100%",
  py: 0.5,
  px: 0.5,
  borderRadius: "6px",
  "& .MuiFormControlLabel-label": {
    fontSize: "0.9rem",
    color: "#c8c8cc",
  },
  "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
  "& .Mui-checked ~ .MuiFormControlLabel-label": {
    color: "#f2f2f2",
    fontWeight: 600,
  },
};

const radioSx = {
  color: "rgba(255,255,255,0.25)",
  p: 0.7,
  "&.Mui-checked": { color: "#faad2b" },
};

const Products = () => {
  const [categorias, setCategories] = useState([]);
  const [categoriesWithID, setCategoriesWithID] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("Todo");
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Controlled search state
  const [update, setUpdate] = useState("");
  const [id, setId] = useState("");
  const [subCategorias, setSubCategorias] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Ref for the Cards container (to scroll to top when page changes)
  const cardsRef = useRef(null);

  useEffect(() => {
    const loadCategories = async () => {
      const [fetchedCategoriesWithID, fetchedCategorias] =
        await fetchCategories();
      setCategoriesWithID(fetchedCategoriesWithID);

      // Ensure "Todo" is the first item in the categories list
      // First, check if "Todo" is already in the fetched categories
      const todoIndex = fetchedCategorias.indexOf("Todo");

      if (todoIndex === -1) {
        // If "Todo" is not in the list, add it at the beginning
        setCategories(["Todo", ...fetchedCategorias]);
      } else if (todoIndex !== 0) {
        // If "Todo" exists but is not at index 0, remove it and add it at the beginning
        const newCategories = [...fetchedCategorias];
        newCategories.splice(todoIndex, 1);
        setCategories(["Todo", ...newCategories]);
      } else {
        // "Todo" is already at the first position
        setCategories(fetchedCategorias);
      }
    };
    loadCategories();
  }, []);

  // Load products when searchQuery, update, or id change
  useEffect(() => {
    const loadProducts = async () => {
      if (searchQuery.length >= 1 || searchQuery === "") {
        setIsSearching(true);
        const data = await fetchProducts(update, id, searchQuery);
        setProducts(data);
        setIsSearching(false);
      }
    };
    loadProducts();
  }, [searchQuery, update, id]);

  // Create a key for Cards so that it resets its internal pagination
  const cardsKey = `${searchQuery}-${selectedCategory}-${selectedSubcategory}`;

  // Callback to scroll to the first product
  const handlePageChange = () => {
    if (cardsRef.current) {
      cardsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Scroll to the Cards component when search/category/subcategory changes
  useEffect(() => {
    if (cardsRef.current) {
      cardsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [cardsKey]);

  // Trigger search manually when clicking the search button
  const handleSearch = () => {
    setSearchQuery(query);
  };

  const handleChangeCategory = (event) => {
    const category = event.target.value;
    setSelectedCategory(category);
    if (category === "Todo") {
      setUpdate(0);
      setSubCategorias([]);
      setSelectedSubcategory("");
    } else {
      const selectedCategoryObj = categoriesWithID.find(
        (cat) => cat.label === category
      );
      if (selectedCategoryObj) {
        setId(selectedCategoryObj.id);
        setUpdate(1);
        setSubCategorias(
          selectedCategoryObj.subcategories.map((sub) => sub.label)
        );
        setSelectedSubcategory("");
      } else {
        setSubCategorias([]);
        setSelectedSubcategory("");
      }
    }
  };

  const handleChangeSubcategory = (event) => {
    const subcategory = event.target.value;
    setSelectedSubcategory(subcategory);
    const selectedCategoryObj = categoriesWithID.find(
      (cat) => cat.label === selectedCategory
    );
    if (selectedCategoryObj) {
      const selectedSubcategoryObj = selectedCategoryObj.subcategories.find(
        (sub) => sub.label === subcategory
      );
      if (selectedSubcategoryObj) {
        setId(selectedSubcategoryObj.id);
      }
    }
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          pt: "79px",
          backgroundColor: "#0b0b0d",
          color: "#f2f2f2",
          minHeight: "100vh",
        }}
      >
        {/* Page title */}
        <Box sx={{ px: { xs: 2, md: 4, lg: 6 }, pt: { xs: 4, md: 6 }, pb: { xs: 2, md: 3 } }}>
          <Typography
            sx={{
              fontSize: { xs: "2rem", md: "2.75rem" },
              fontWeight: 600,
              letterSpacing: "-0.02em",
            }}
          >
            Catálogo
          </Typography>
        </Box>

        <Box
          sx={{
            px: { xs: 2, md: 4, lg: 6 },
            pb: 8,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 5 },
          }}
        >
          {/* Categories Menu */}
          <Box
            component="aside"
            sx={{
              width: { xs: "100%", md: "220px" },
              flexShrink: 0,
              borderRight: { md: "1px solid rgba(255,255,255,0.08)" },
              pr: { md: 3 },
              pb: { xs: 2, md: 0 },
              borderBottom: { xs: "1px solid rgba(255,255,255,0.08)", md: "none" },
            }}
          >
            <FormControl fullWidth>
              <Typography component="span" sx={filterLabelSx}>
                Categorías
              </Typography>
              <RadioGroup
                aria-labelledby="RBGroup-label"
                value={selectedCategory}
                name="RBGroup_categorias"
                onChange={handleChangeCategory}
              >
                {categorias.map((categoria, index) => (
                  <FormControlLabel
                    key={index}
                    value={categoria}
                    control={<Radio sx={radioSx} size="small" />}
                    label={categoria}
                    sx={filterRowSx}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Main Content */}
          <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
            {/* Search Bar & Subcategories */}
            <Box component="section" sx={{ mb: 3 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                alignItems={{ xs: "stretch", sm: "flex-end" }}
                justifyContent="flex-end"
              >
                <FormControl variant="standard" sx={{ minWidth: 160 }}>
                  <Select
                    value={selectedSubcategory}
                    onChange={handleChangeSubcategory}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                    sx={{
                      color: "#f2f2f2",
                      fontSize: "0.9rem",
                      "&:before": { borderColor: "rgba(255,255,255,0.18)" },
                      "&:hover:not(.Mui-disabled):before": { borderColor: "rgba(255,255,255,0.4)" },
                      "&:after": { borderColor: "#faad2b" },
                      "& .MuiSvgIcon-root": { color: "#9a9a9e" },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: { backgroundColor: "#161618", color: "#f2f2f2" },
                      },
                    }}
                  >
                    {subCategorias.map((subcategoria, index) => (
                      <MenuItem key={index} value={subcategoria}>
                        {subcategoria}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ color: "#6f6f74" }}>
                    Subcategoría
                  </FormHelperText>
                </FormControl>
                <TextField
                  label="Buscar..."
                  variant="standard"
                  value={query}
                  autoComplete="off"
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  sx={{
                    width: { xs: "100%", sm: "220px" },
                    "& .MuiInputBase-input": { color: "#f2f2f2" },
                    "& .MuiInput-underline:before": { borderColor: "rgba(255,255,255,0.18)" },
                    "& .MuiInput-underline:hover:before": { borderColor: "rgba(255,255,255,0.4)" },
                    "& .MuiInput-underline:after": { borderColor: "#faad2b" },
                    "& label": { color: "#6f6f74" },
                    "& label.Mui-focused": { color: "#faad2b" },
                  }}
                />

                <IconButton aria-label="search" onClick={handleSearch} sx={{ color: "#9a9a9e" }}>
                  <SearchIcon />
                </IconButton>
              </Stack>
            </Box>

            {/* Searching indicator */}
            {isSearching && (
              <Box sx={{ mb: 2, color: "#9a9a9e" }}>Buscando...</Box>
            )}

            {/* Products */}
            <Box ref={cardsRef}>
              {/*
                Pass onPageChange to Cards so that whenever its page changes,
                it calls handlePageChange to scroll to the top.
              */}
              <Cards
                key={cardsKey}
                products={products}
                onPageChange={handlePageChange}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default Products;
